import { SelectOption } from "./Select";
import SearchInput from "./SearchInput";
import Input from "./Input";
import Button from "../buttons/Button";
import { X } from "lucide-react";

export interface ShareOption extends SelectOption {
  split_percentage: string;
}

interface SplitSharesProps {
  shares: ShareOption[];
  onChange: (shares: ShareOption[]) => void;
  reloadPropName: string;
  queryParam: string;
  placeholder?: string;
  errors?: string;
  baseErrors?: string;
}

const SplitShares: React.FC<SplitSharesProps> = ({
  shares,
  onChange,
  reloadPropName,
  queryParam,
  placeholder = "Search for contributors",
  errors,
  baseErrors,
}) => {
  const handleSelect = (option: SelectOption) => {
    if (shares.find((s) => s.value === option.value)) return;
    onChange([...shares, { ...option, split_percentage: "" }]);
  };

  const handleRemove = (index: number) => {
    onChange(shares.filter((_, i) => i !== index));
  };

  const handleSplitChange = (index: number, value: string) => {
    onChange(
      shares.map((s, i) =>
        i === index ? { ...s, split_percentage: value } : s,
      ),
    );
  };

  const handleSplitEvenly = () => {
    const n = shares.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    onChange(
      shares.map((s, i) => ({
        ...s,
        split_percentage: String(base + (i >= n - remainder ? 1 : 0)),
      })),
    );
  };

  const totalSplit = shares.reduce(
    (sum, s) => sum + (parseFloat(s.split_percentage) || 0),
    0,
  );

  return (
    <div className="flex w-full flex-col gap-10">
      <SearchInput
        placeholder={placeholder}
        reloadPropName={reloadPropName}
        queryParam={queryParam}
        errors={errors}
        onSelect={handleSelect}
        selectedValues={shares.map((s) => s.value)}
      />
      {shares.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
              Split Percentages
            </label>
            <Button
              text="Split evenly"
              type="secondary"
              htmlButtonType="button"
              className="w-full md:w-auto"
              onClick={handleSplitEvenly}
            />
          </div>
          {shares.map((share, index) => (
            <div key={share.value} className="flex items-center gap-3">
              <span className="text-cream-800 dark:text-cream-200 min-w-0 flex-1 truncate text-sm font-medium">
                {share.label}
              </span>
              <div className="w-20 shrink-0">
                <Input
                  type="number"
                  placeholder="e.g. 50"
                  value={share.split_percentage}
                  onChange={(e) => handleSplitChange(index, e.target.value)}
                />
              </div>
              <span className="text-cream-600 dark:text-cream-400 shrink-0 text-sm">
                %
              </span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-cream-600 hover:text-cream-900 dark:text-cream-400 dark:hover:text-cream-100 shrink-0 transition-colors"
                aria-label={`Remove ${share.label}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <p
            className={`text-sm font-semibold ${
              totalSplit === 100
                ? "text-green-600 dark:text-green-400"
                : "text-peach-600 dark:text-peach-400"
            }`}
          >
            Total: {totalSplit}%
          </p>
        </div>
      )}
      {baseErrors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {baseErrors}
        </span>
      )}
    </div>
  );
};

export default SplitShares;
